import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

/**
 * ONE place that draws PDF pages onto canvases, shared by every in-app
 * viewer (placement editor, progress view, plain overlay). It existed three
 * times, each rendering a page at ~760 CSS px x devicePixelRatio, which on a
 * 1x screen means a landscape sheet with 8 pt text is drawn at about 7 px
 * and looks soft however sharp the PDF itself is.
 *
 * Two changes, both here so they apply everywhere:
 *   - the canvas is rendered at no less than 2x the display size (3x on
 *     retina is just devicePixelRatio), so text stays crisp even when the
 *     page is scaled or zoomed by the browser;
 *   - the base page is wider and every viewer can zoom (see ZoomControl).
 *
 * Box positions elsewhere are stored as FRACTIONS of the page, so changing
 * the displayed width never moves a signature box.
 */

export const PAGE_BASE_WIDTH = 900;

/** A canvas past this is slow or refused outright by some browsers. */
const MAX_CANVAS_PX = 4096;

export function renderPdfPages(
  pdf: PDFDocumentProxy,
  getCanvas: (page: number) => HTMLCanvasElement | undefined,
  cssWidth: number,
  /** The canvas fills its container (100%) instead of a fixed pixel width -
   *  `cssWidth` then only sets how many pixels are drawn. */
  fluid = false,
): { done: Promise<void>; cancel: () => void } {
  let cancelled = false;
  let task: RenderTask | null = null;

  const done = (async () => {
    for (let n = 1; n <= pdf.numPages; n += 1) {
      if (cancelled) return;
      const canvas = getCanvas(n);
      if (!canvas) continue;
      const page = await pdf.getPage(n);
      if (cancelled) return;

      const base = page.getViewport({ scale: 1 });
      const density = Math.max(window.devicePixelRatio || 1, 2);
      const scale = Math.min((cssWidth / base.width) * density, MAX_CANVAS_PX / base.width);
      const viewport = page.getViewport({ scale });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      if (fluid) {
        canvas.style.width = '100%';
        canvas.style.height = '';
        canvas.style.aspectRatio = `${base.width} / ${base.height}`;
      } else {
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${(cssWidth * base.height) / base.width}px`;
      }

      task = page.render({ canvasContext: canvas.getContext('2d')!, viewport });
      try {
        await task.promise;
      } catch (error) {
        // A cancelled render (zoom changed mid-page) is not a failure.
        if (cancelled) return;
        throw error;
      }
    }
  })().catch(() => undefined);

  return {
    done,
    cancel: () => {
      cancelled = true;
      task?.cancel();
    },
  };
}
